import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  addDoc 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

const SUMMIT_COLLECTION = 'summit';
const REGISTRATIONS_COLLECTION = 'summit_registrations';

// Get summit event details
export const getSummitEventDetails = async () => {
  try {
    const summitRef = doc(db, SUMMIT_COLLECTION, 'event_details');
    const summitDoc = await getDoc(summitRef);
    
    if (summitDoc.exists()) {
      return summitDoc.data();
    }
    
    // Return default data if no document exists
    return {
      startDate: '2024-07-15',
      endDate: '2024-07-17',
      location: 'Nashville Convention Center, Nashville, TN',
      overview: {
        whyAttend: 'Join industry leaders and experts for three days of intensive learning, networking, and strategic planning. The BBA Summit is your opportunity to gain exclusive insights, connect with peers, and accelerate your business growth.',
        knowledgeSkills: [
          'Advanced business strategy and scaling techniques',
          'Latest industry trends and market analysis',
          'Financial management and optimization strategies',
          'Team building and leadership development',
          'Technology integration and automation',
          'Customer acquisition and retention strategies'
        ],
        resourcesSupport: [
          'Exclusive access to BBA resource library',
          'One-on-one consulting sessions',
          'Networking events and peer groups',
          'Follow-up support and accountability',
          'Access to expert panel discussions',
          'Comprehensive event materials and recordings'
        ]
      },
      schedule: [
        { day: 'Day 1 - July 15', events: ['Opening Keynote (9:00 AM)', 'Strategy Sessions (10:30 AM)', 'Networking Lunch (12:00 PM)', 'Workshop Sessions (2:00 PM)', 'Evening Reception (6:00 PM)'] },
        { day: 'Day 2 - July 16', events: ['Morning Keynote (8:30 AM)', 'Breakout Sessions (10:00 AM)', 'Panel Discussions (2:00 PM)', 'Interactive Workshops (4:00 PM)', 'Awards Dinner (7:00 PM)'] },
        { day: 'Day 3 - July 17', events: ['Closing Keynote (9:00 AM)', 'Strategy Implementation (10:30 AM)', 'Q&A Sessions (2:00 PM)', 'Closing Ceremony (4:00 PM)'] }
      ],
      speakers: [
        { name: 'Dr. Sarah Johnson', title: 'Business Strategy Expert', company: 'Johnson Consulting Group', bio: 'Leading authority on business scaling and strategic planning with 20+ years of experience.' },
        { name: 'Michael Chen', title: 'Technology Integration Specialist', company: 'TechBuild Solutions', bio: 'Expert in digital transformation and automation for construction businesses.' },
        { name: 'Lisa Rodriguez', title: 'Financial Management Advisor', company: 'Rodriguez Financial Group', bio: 'Specialist in financial optimization and growth strategies for small businesses.' },
        { name: 'David Thompson', title: 'Leadership Development Coach', company: 'Thompson Leadership Institute', bio: 'Dedicated to building high-performing teams and developing leadership skills.' }
      ],
      sponsors: [
        { name: 'Construction Pro Tools', tier: 'Platinum', logo: 'CPT Logo', description: 'Leading provider of construction management software and tools.' },
        { name: 'BuildTech Solutions', tier: 'Gold', logo: 'BTS Logo', description: 'Innovative technology solutions for the construction industry.' },
        { name: 'SafetyFirst Equipment', tier: 'Silver', logo: 'SFE Logo', description: 'Premium safety equipment and training solutions.' }
      ],
      learningTracks: [
        { name: 'Business Strategy & Scaling', sessions: ['Strategic Planning Workshop', 'Growth Mindset Development', 'Market Analysis Techniques'] },
        { name: 'Financial Management', sessions: ['Profit Optimization', 'Cash Flow Management', 'Investment Strategies'] },
        { name: 'Technology & Automation', sessions: ['Digital Transformation', 'Process Automation', 'Technology Integration'] },
        { name: 'Leadership & Team Building', sessions: ['Leadership Development', 'Team Dynamics', 'Communication Skills'] }
      ],
      pricingPlans: [
        { name: 'Early Bird', price: 1295, originalPrice: 1495, availableUntil: '2024-06-15', features: ['Full conference access', 'All workshop materials', 'Networking events', 'Resource library access'] },
        { name: 'Regular', price: 1495, originalPrice: 1495, availableUntil: '2024-07-10', features: ['Full conference access', 'All workshop materials', 'Networking events', 'Resource library access'] },
        { name: 'Group Rate', price: 1195, originalPrice: 1495, availableUntil: '2024-07-10', features: ['Full conference access', 'All workshop materials', 'Networking events', 'Resource library access', 'Group discount'] }
      ],
      updatedAt: serverTimestamp()
    };
  } catch (error) {
    console.error('Error getting summit event details:', error);
    throw error;
  }
};

// Save summit event details
export const saveSummitEventDetails = async (eventDetails) => {
  try {
    const summitRef = doc(db, SUMMIT_COLLECTION, 'event_details');
    await setDoc(summitRef, {
      ...eventDetails,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error saving summit event details:', error);
    throw error;
  }
};

// Get whether the BBA Summit card should show on every client's dashboard
export const getSummitDashboardVisibility = async () => {
  try {
    const settingsRef = doc(db, SUMMIT_COLLECTION, 'dashboard_settings');
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      // Default to visible when the field has never been written
      return settingsDoc.data().showOnDashboard !== false;
    }

    return true;
  } catch (error) {
    console.error('Error getting summit dashboard visibility:', error);
    // Fail open so a read error never hides the card
    return true;
  }
};

// Show/hide the BBA Summit card on every client's dashboard
export const setSummitDashboardVisibility = async (showOnDashboard) => {
  try {
    const settingsRef = doc(db, SUMMIT_COLLECTION, 'dashboard_settings');
    await setDoc(settingsRef, {
      showOnDashboard: !!showOnDashboard,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error saving summit dashboard visibility:', error);
    throw error;
  }
};

// Get all registrations
export const getSummitRegistrations = async () => {
  try {
    const registrationsRef = collection(db, REGISTRATIONS_COLLECTION);
    const q = query(registrationsRef, orderBy('registrationDate', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        registrationDate: doc.data().registrationDate?.toDate?.() || doc.data().registrationDate
      }))
      .filter(reg => !reg.deleted); // Filter out deleted registrations
  } catch (error) {
    console.error('Error getting summit registrations:', error);
    throw error;
  }
};

// Add new registration
export const addSummitRegistration = async (registrationData) => {
  try {
    const registrationsRef = collection(db, REGISTRATIONS_COLLECTION);
    const docRef = await addDoc(registrationsRef, {
      ...registrationData,
      createdAt: serverTimestamp(),
      registrationDate: serverTimestamp()
    });
    
    // Also save registration data to user document for persistence
    if (registrationData.userId) {
      const userRef = doc(db, 'users', registrationData.userId);
      await updateDoc(userRef, {
        summitRegistration: {
          id: docRef.id,
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          title: registrationData.title,
          email: registrationData.email,
          userEmail: registrationData.userEmail,
          guests: registrationData.guests || [],
          registrationDate: serverTimestamp(),
          lastUpdated: serverTimestamp()
        },
        lastUpdated: serverTimestamp()
      });
    }
    
    // Trigger summit registration notification
    try {
      const { triggerSummitRegistrationNotification } = await import('./notificationTriggerService');
      await triggerSummitRegistrationNotification({
        registrantName: `${registrationData.firstName} ${registrationData.lastName}`,
        email: registrationData.email,
        company: registrationData.company || 'Unknown Company',
        summitName: 'BBA Summit',
        registrationDate: new Date().toISOString(),
        ticketType: 'General Admission',
        title: registrationData.title,
        guests: registrationData.guests || [],
        timestamp: new Date().toISOString()
      });
      console.log('✅ Summit registration notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending summit registration notification:', notificationError);
      // Don't fail the registration if notification fails
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding summit registration:', error);
    throw error;
  }
};

// Get registration statistics
export const getSummitStats = async () => {
  try {
    const registrations = await getSummitRegistrations();
    
    const totalRegistrations = registrations.length;
    const targetRegistrations = 500;
    const revenue = registrations.reduce((sum, reg) => sum + (reg.amount || 0), 0);
    const targetRevenue = 750000;
    
    // Calculate days until event (hardcoded for now)
    const eventDate = new Date('2024-07-15');
    const today = new Date();
    const daysUntilEvent = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    
    // Calculate registration by guest count
    const byGuestCount = {};
    registrations.forEach(reg => {
      const guestCount = reg.guests ? reg.guests.length + 1 : 1;
      if (!byGuestCount[guestCount]) {
        byGuestCount[guestCount] = { count: 0, registrations: 0 };
      }
      byGuestCount[guestCount].count += guestCount;
      byGuestCount[guestCount].registrations++;
    });
    
    // Convert to array format
    const guestCountArray = Object.entries(byGuestCount).map(([count, data]) => ({
      type: `${count} Guest${count > 1 ? 's' : ''}`,
      count: data.registrations,
      totalGuests: data.count,
      percentage: totalRegistrations > 0 ? (data.registrations / totalRegistrations) * 100 : 0
    }));
    
    return {
      totalRegistrations,
      targetRegistrations,
      revenue,
      targetRevenue,
      daysUntilEvent: Math.max(0, daysUntilEvent),
      registrationTrend: 'up',
      revenueTrend: 'up',
      byGuestCount: guestCountArray
    };
  } catch (error) {
    console.error('Error getting summit stats:', error);
    throw error;
  }
};

// Check if a specific user is registered for the summit
export const checkUserSummitRegistration = async (userEmail) => {
  try {
    if (!userEmail) return false;
    
    const registrationsRef = collection(db, REGISTRATIONS_COLLECTION);
    // Scoped to the signed-in login: clients may only read their own registration
    const q = query(registrationsRef, where('userId', '==', getAuth().currentUser?.uid || ''), where('userEmail', '==', userEmail));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return false;
    }
    
    // Check if the registration is deleted
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    // Return false if registration is deleted
    if (data.deleted) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking user summit registration:', error);
    throw error;
  }
};

// Get full registration data for a specific user
export const getUserSummitRegistration = async (userEmail) => {
  try {
    if (!userEmail) return null;
    
    const registrationsRef = collection(db, REGISTRATIONS_COLLECTION);
    // Scoped to the signed-in login: clients may only read their own registration
    const q = query(registrationsRef, where('userId', '==', getAuth().currentUser?.uid || ''), where('userEmail', '==', userEmail));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first (and should be only) registration for this user
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    // Check if registration is deleted
    if (data.deleted) {
      return null;
    }
    
    return {
      id: doc.id,
      ...data,
      registrationDate: data.registrationDate?.toDate?.() || data.registrationDate
    };
  } catch (error) {
    console.error('Error getting user summit registration:', error);
    throw error;
  }
};

// Get summit registration from user document (for persistence)
export const getUserSummitRegistrationFromUser = async (userId) => {
  try {
    if (!userId) return null;
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    return userData.summitRegistration || null;
  } catch (error) {
    console.error('Error getting user summit registration from user document:', error);
    throw error;
  }
};

// Update summit registration in both collections
export const updateSummitRegistration = async (registrationId, registrationData) => {
  try {
    // Update in summit_registrations collection
    const registrationRef = doc(db, REGISTRATIONS_COLLECTION, registrationId);
    await updateDoc(registrationRef, {
      ...registrationData,
      lastUpdated: serverTimestamp()
    });
    
    // Also update in user document for persistence
    if (registrationData.userId) {
      const userRef = doc(db, 'users', registrationData.userId);
      await updateDoc(userRef, {
        summitRegistration: {
          id: registrationId,
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          title: registrationData.title,
          email: registrationData.email,
          userEmail: registrationData.userEmail,
          guests: registrationData.guests || [],
          registrationDate: registrationData.registrationDate,
          lastUpdated: serverTimestamp()
        },
        lastUpdated: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating summit registration:', error);
    throw error;
  }
};

// Delete summit registration from both collections
export const deleteSummitRegistration = async (registrationId, userId) => {
  try {
    // Mark as deleted in summit_registrations collection
    const registrationRef = doc(db, REGISTRATIONS_COLLECTION, registrationId);
    await updateDoc(registrationRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
    
    // Remove from user document
    if (userId) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        summitRegistration: null,
        lastUpdated: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting summit registration:', error);
    throw error;
  }
}; 